/**
 * Faixa "Filtros aplicados" no topo das listas.
 *  - azul: há filtros (só informa);
 *  - amarela: tela de risco com itens fora do filtro, ou período que não inclui hoje;
 *  - vermelha: algo ATRASADO / vencendo hoje ficou fora do filtro.
 * Sempre com "Limpar filtros" (volta ao padrão da tela).
 */
import type { ReactNode } from "react";
import { staleRange, useRememberedSearch } from "@/lib/prefs";
import { useLookups } from "@/lib/catalog";

export interface FilterItem { label: string; value?: ReactNode }
export interface FilterRisk {
  /** Itens que existem mas não aparecem por causa do filtro (ex.: intimações em aberto). */
  hidden: number;
  /** Desses, quantos são urgentes (atrasados, vencendo hoje…). */
  urgent?: number;
  /** "intimação em aberto" / "intimações em aberto". */
  noun: [string, string];
  urgentNoun?: [string, string];
}

const plural = (n: number, [one, many]: [string, string]) => `${n} ${n === 1 ? one : many}`;

export function FilterNotice({ items, onClear, restored, stale, risk }: {
  items: FilterItem[]; onClear: () => void; restored?: boolean; stale?: string | null; risk?: FilterRisk | null;
}): JSX.Element | null {
  if (!items.length && !stale) return null;
  const hidden = risk && risk.hidden > 0 ? risk.hidden : 0;
  const urgent = risk && (risk.urgent ?? 0) > 0 ? risk.urgent! : 0;
  const tone = urgent ? "bad" : hidden || stale ? "warn" : "info";
  return (
    <div className={`filter-notice ${tone}`} role={tone === "info" ? "status" : "alert"}>
      <div style={{ flex: 1 }}>
        {items.length > 0 && <div>
          <b>Filtros aplicados{restored ? " (lembrados da última vez)" : ""}:</b>{" "}
          {items.map((it, i) => <span key={i}>{i ? " · " : ""}{it.label}{it.value !== undefined && it.value !== "" ? <> = <b>{it.value}</b></> : null}</span>)}
        </div>}
        {stale && <div>⚠ {stale}</div>}
        {urgent > 0 && <div>⛔ {plural(urgent, risk!.urgentNoun ?? ["item urgente", "itens urgentes"])} fora deste filtro.</div>}
        {hidden > 0 && <div>{urgent ? "No total, " : "⚠ "}{plural(hidden, risk!.noun)} não {hidden === 1 ? "aparece" : "aparecem"} por causa do filtro.</div>}
      </div>
      <button type="button" className="btn small" onClick={onClear}>Limpar filtros</button>
    </div>
  );
}

/**
 * Para telas com filtros na URL: lembra/restaura os filtros (useRememberedSearch) e mostra a faixa.
 * `labels`: nome de cada filtro e como mostrar o valor.
 */
type Labels = Record<string, (v: string) => [string, ReactNode?]>;
export function RememberedFilterNotice({ screen, keys, labels, dateKeys, onClear }: {
  screen: string; keys: string[]; labels: Labels | ((lk: ReturnType<typeof useLookups>) => Labels); dateKeys?: string[]; onClear?: () => void;
}): JSX.Element | null {
  const lk = useLookups();
  const r = useRememberedSearch(screen, keys, { dateKeys });
  if (typeof labels === "function") labels = labels(lk);
  const L = labels;
  const items = r.active.filter((k) => L[k]).map((k) => { const [label, value] = L[k]!(r.values[k]!); return { label, value }; });
  const from = dateKeys?.[0] ? r.values[dateKeys[0]] : undefined;
  const to = dateKeys?.[1] ? r.values[dateKeys[1]] : undefined;
  return <FilterNotice items={items} restored={r.restored} stale={from || to ? staleRange(from, to) : null} onClear={() => { r.clear(); onClear?.(); }} />;
}
